import React, { useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import "./ManagePageViewSimple.scss";

interface ManagePageViewSimpleProps {
  savedViews: Array<any>;
  onApplyView?: (view: any) => void;
  onTogglePin?: (view: any) => void;
  onToggleDefault?: (view: any) => void;
  onRenameView?: (view: any) => void;
  onDeleteView?: (view: any) => void;
  onReorderViews?: (
    moves: Array<{ id: number; old_rank: number | null; new_rank: number }>,
  ) => Promise<boolean>;
}

const formatUpdatedText = (view: any): string => {
  const rawDate =
    view?.updated_at ||
    view?.created_at ||
    view?.date_updated ||
    view?.date_created ||
    view?.createdAt;
  if (!rawDate) {
    return "Updated On --";
  }

  return `Updated On ${rawDate}`;
};

const ManagePageViewSimple: React.FC<ManagePageViewSimpleProps> = ({
  savedViews,
  onApplyView,
  onTogglePin,
  onToggleDefault,
  onRenameView,
  onDeleteView,
  onReorderViews,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [draggedId, setDraggedId] = useState<number | null>(null);

  const rows = useMemo(
    () =>
      [...(savedViews ?? [])].sort(
        (a, b) => (a?.view_order ?? 9999) - (b?.view_order ?? 9999),
      ),
    [savedViews],
  );

  const filteredRows = useMemo(
    () =>
      rows.filter((item) =>
        String(item?.view_name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ),
    [rows, searchTerm],
  );

  const canReorder = searchTerm.trim().length === 0;

  const getViewId = (view: any): number | null => {
    const candidate = view?.seqNo ?? view?.id ?? view?.seq_no;
    const parsed = Number(candidate);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleDragStart = (view: any) => {
    if (!canReorder) return;
    const viewId = getViewId(view);
    if (viewId === null) return;
    setDraggedId(viewId);
  };

  const handleDrop = async (targetView: any) => {
    if (!canReorder || !onReorderViews || draggedId === null) {
      setDraggedId(null);
      return;
    }

    const targetId = getViewId(targetView);
    if (targetId === null || targetId === draggedId) {
      setDraggedId(null);
      return;
    }

    const fromIndex = rows.findIndex((item) => getViewId(item) === draggedId);
    const toIndex = rows.findIndex((item) => getViewId(item) === targetId);

    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      setDraggedId(null);
      return;
    }

    const movedId = getViewId(rows[fromIndex]);
    if (movedId === null) {
      setDraggedId(null);
      return;
    }

    await onReorderViews([
      {
        id: movedId,
        old_rank: fromIndex + 1,
        new_rank: toIndex + 1,
      },
    ]);

    setDraggedId(null);
  };

  return (
    <div className="manage-page-view-simple">
      <div className="mpv-description">
        To edit or delete existing saved page views, utilize the controls on the
        individual line items. Mark a page view as your default view. You can
        only have one default view at a time. Utilize the pin icon to display up
        to 10 of your saved views in the dropdown on the Dashboard page.
      </div>

      <div className="mpv-search-wrap">
        <input
          className="mpv-search-input"
          placeholder="Search views"
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search views"
          value={searchTerm}
        />
        <SearchIcon
          className="mpv-search-icon"
          sx={{ width: 16, height: 16 }}
        />
      </div>

      <div className="mpv-section-header">
        <span>PINNED (DISPLAYS IN THE DROPDOWN LIST)</span>
        <span>{`(${filteredRows.length} OUT OF ${rows.length})`}</span>
      </div>

      <div className="mpv-list">
        {filteredRows.length === 0 ? (
          <div className="mpv-row">
            <div className="mpv-name">No saved views found.</div>
          </div>
        ) : null}
        {filteredRows.map((view) => (
          <div
            className="mpv-row"
            key={String(view?.seq_no ?? view?.view_order ?? view?.view_name)}
            draggable={canReorder}
            onDragStart={() => handleDragStart(view)}
            onDragOver={(e) => {
              if (canReorder) {
                e.preventDefault();
              }
            }}
            onDrop={() => handleDrop(view)}
          >
            <div className="mpv-left">
              <DragIndicatorOutlinedIcon sx={{ width: 16, height: 16 }} />
              <div className="mpv-name-block">
                <div className="mpv-name-line">
                  <span className="mpv-name">
                    {view?.view_name || "Untitled view"}
                  </span>
                  <button
                    type="button"
                    className="mpv-icon-btn"
                    aria-label="Edit view"
                    onClick={() => onRenameView?.(view)}
                  >
                    <EditOutlinedIcon sx={{ width: 14, height: 14 }} />
                  </button>
                </div>
                <span className="mpv-updated">{formatUpdatedText(view)}</span>
              </div>
            </div>

            <div className="mpv-actions">
              <button
                type="button"
                className="mpv-icon-btn"
                aria-label="Apply view"
                onClick={() => onApplyView?.(view)}
              >
                <PublishOutlinedIcon sx={{ width: 16, height: 16 }} />
              </button>
              <button
                type="button"
                className="mpv-icon-btn"
                aria-label="Pin view"
                onClick={() => onTogglePin?.(view)}
              >
                {view?.is_pinned ? (
                  <PushPinIcon sx={{ width: 16, height: 16 }} />
                ) : (
                  <PushPinOutlinedIcon sx={{ width: 16, height: 16 }} />
                )}
              </button>
              <button
                type="button"
                className="mpv-icon-btn"
                aria-label="Default view"
                onClick={() => onToggleDefault?.(view)}
              >
                {view?.is_fav || view?.is_default ? (
                  <StarIcon sx={{ width: 16, height: 16 }} />
                ) : (
                  <StarBorderOutlinedIcon sx={{ width: 16, height: 16 }} />
                )}
              </button>
              <button
                type="button"
                className="mpv-icon-btn"
                aria-label="Delete view"
                onClick={() => onDeleteView?.(view)}
              >
                <DeleteOutlineOutlinedIcon sx={{ width: 16, height: 16 }} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ManagePageViewSimple;
